import * as React from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { ErrorPage, ErrorBoundaryWrapper } from '@web-config-app/core-react-ui';
import { AdminToolsSchemaPageComponent } from '@web-config-app/core-react-containers';
import {
  useConfigApp,
  useConfigAppParams,
  useAuthoringEnvironments,
  useLocales,
} from '@web-config-app/core-react';
import type {
  ResolvedModule,
  SchemaPageModule,
  ComponentPageModule,
} from '@web-config-app/core';
import { PermissionsGuard } from '../permissions-guard/permissions-guard.component';
import { moduleComponents } from '../../configs/module-components';

const isSchemaPage = (
  resolvedModule: ResolvedModule,
): resolvedModule is SchemaPageModule => resolvedModule.type === 'schemaPage';

const isComponentPage = (
  resolvedModule: ResolvedModule,
): resolvedModule is ComponentPageModule =>
  resolvedModule.type === 'componentPage';

/**
 * Gathers the tenant/environment/locale context that every module page
 * component receives as props.
 */
const useModulePageContext = () => {
  const { tenantId } = useConfigApp();
  const { tabId = '' } = useConfigAppParams();
  const { environmentMap, baseAuthoringEnvironment } =
    useAuthoringEnvironments();
  const { locales } = useLocales();

  const environments = React.useMemo(() => {
    if (environmentMap) {
      return Array.from(environmentMap.values());
    }
    return [baseAuthoringEnvironment];
  }, [environmentMap, baseAuthoringEnvironment]);

  const localeCodes = React.useMemo(
    () => (locales ?? []).map((locale) => locale.code),
    [locales],
  );

  return { tenantId, tabId, environments, localeCodes };
};

// ---------------------------------------------------------------------------
// Page element renderers — one per non-entity module type
// ---------------------------------------------------------------------------

/**
 * Looks up the registered React component by module ID and renders it
 * with resolved params, environment info, and permission context.
 */
const ComponentPageElement = ({
  componentPageId,
  params,
  permissions,
  parentModuleId,
}: {
  componentPageId: string;
  params?: Record<string, unknown>;
  permissions?: string[];
  parentModuleId?: string;
}) => {
  const { tenantId, tabId, environments, localeCodes } = useModulePageContext();
  const PageComponent = moduleComponents[componentPageId];
  if (!PageComponent) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        `Component page "${componentPageId}" is declared in the module registry but is not registered in moduleComponents (apps/config-app/src/configs/module-components.ts). Rendering an error page.`,
      );
    }
    return <ErrorPage />;
  }

  return (
    <PermissionsGuard permissions={permissions}>
      <ErrorBoundaryWrapper>
        <React.Suspense fallback={null}>
          <PageComponent
            params={params ?? {}}
            tenantId={tenantId}
            tabId={tabId}
            environments={environments}
            locales={localeCodes}
            parentModuleId={parentModuleId}
            permissions={permissions ?? []}
          />
        </React.Suspense>
      </ErrorBoundaryWrapper>
    </PermissionsGuard>
  );
};

/**
 * Adapts a SchemaPageModule into the AdminToolsSchemaPage config shape
 * consumed by AdminToolsSchemaPageComponent.
 */
const SchemaPageElement = ({
  schemaModule,
}: {
  schemaModule: SchemaPageModule;
}) => (
  <PermissionsGuard permissions={schemaModule.permissions}>
    <AdminToolsSchemaPageComponent
      config={{
        type: 'schema',
        path: schemaModule.id,
        labelKey: schemaModule.labelKey,
        icon: schemaModule.icon,
        schema: schemaModule.schema,
        endpoint: {
          path: schemaModule.operation.path,
          resourceType: schemaModule.operation.resourceType ?? schemaModule.id,
          ...(schemaModule.operation.method && {
            method: schemaModule.operation.method,
          }),
        },
        submitLabelKey: schemaModule.operation.label?.key ?? 'SUBMIT',
        successMessageKey:
          schemaModule.operation.successMessage?.key ?? 'SUCCESS',
        enableEnvironmentSelection: schemaModule.environmentSelection,
      }}
    />
  </PermissionsGuard>
);

/**
 * Dispatches a resolved module to the correct page element renderer.
 * Entity modules are never routed here — they use explicit route patterns
 * in AppRoutes — so hitting the fallback ErrorPage means the module tree
 * contains an unexpected type.
 *
 * `parentModuleId` is the URL module segment that sits above this
 * module — set by the caller when this module is nested inside a
 * container so component pages know their hierarchical context.
 */
const ModuleElement = ({
  resolvedModule,
  parentModuleId,
}: {
  resolvedModule: ResolvedModule;
  parentModuleId?: string;
}) => {
  if (isSchemaPage(resolvedModule)) {
    return <SchemaPageElement schemaModule={resolvedModule} />;
  }

  if (isComponentPage(resolvedModule)) {
    return (
      <ComponentPageElement
        componentPageId={resolvedModule.id}
        params={resolvedModule.params}
        permissions={resolvedModule.permissions}
        parentModuleId={parentModuleId}
      />
    );
  }

  return <ErrorPage />;
};

/**
 * Redirects from a container's index to its first accessible child. For
 * entity children, builds the full entity list path including the
 * environment key. For non-entity children (schema pages, component
 * pages), navigates to the child's ID segment. Entities filtered out of
 * `entityConfigs` by permissions are skipped so the redirect never lands
 * on a dead route.
 */
const ContainerIndexRedirect = ({
  childModules,
}: {
  childModules: ResolvedModule[];
}) => {
  const { defaultAuthoringEnvironment } = useAuthoringEnvironments();
  const { entityConfigs = {} } = useConfigApp();
  const firstChild = childModules.find(
    (child) => child.type !== 'entity' || child.id in entityConfigs,
  );
  if (!firstChild) return <ErrorPage />;

  if (firstChild.type === 'entity') {
    return (
      <Navigate
        to={`${firstChild.id}/environments/${defaultAuthoringEnvironment.environmentKey}/list`}
        replace
      />
    );
  }

  return <Navigate to={firstChild.id} replace />;
};

interface ModuleRoutesProps {
  modules: ResolvedModule[];
}

/**
 * Recursive route generator for non-entity modules within a tab.
 * Handles schema pages, component pages, and container redirects.
 * Entity routes are handled by explicit route patterns in AppRoutes.
 */
export const ModuleRoutes = ({ modules }: ModuleRoutesProps) => (
  <Routes>
    {modules.map((resolvedModule) => {
      // Container with children: render an Outlet layout with a child
      // route per module. The index redirects to the first child
      // (entity list path for entities, bare ID for other types).
      if (
        resolvedModule.type === 'container' &&
        resolvedModule.modules &&
        resolvedModule.modules.length > 0
      ) {
        return (
          <Route
            key={resolvedModule.id}
            path={resolvedModule.id}
            element={<Outlet />}
          >
            <Route
              index
              element={
                <ContainerIndexRedirect childModules={resolvedModule.modules} />
              }
            />
            {resolvedModule.modules.map((child) => (
              <Route key={child.id} path={child.id}>
                <Route
                  index
                  element={
                    <ModuleElement
                      resolvedModule={child}
                      parentModuleId={resolvedModule.id}
                    />
                  }
                />
                <Route
                  path="*"
                  element={
                    <ModuleElement
                      resolvedModule={child}
                      parentModuleId={resolvedModule.id}
                    />
                  }
                />
              </Route>
            ))}
            <Route path="*" element={<ErrorPage />} />
          </Route>
        );
      }

      // Component page with children: renders its own page at the index
      // and exposes children as nested routes (e.g. a landing page with
      // sub-navigation).
      if (
        isComponentPage(resolvedModule) &&
        resolvedModule.modules &&
        resolvedModule.modules.length > 0
      ) {
        return (
          <Route
            key={resolvedModule.id}
            path={resolvedModule.id}
            element={<Outlet />}
          >
            <Route
              index
              element={
                <ComponentPageElement
                  componentPageId={resolvedModule.id}
                  params={resolvedModule.params}
                  permissions={resolvedModule.permissions}
                  parentModuleId={resolvedModule.id}
                />
              }
            />
            {resolvedModule.modules.map((child) => (
              <Route key={child.id} path={child.id}>
                <Route
                  index
                  element={
                    <ModuleElement
                      resolvedModule={child}
                      parentModuleId={resolvedModule.id}
                    />
                  }
                />
                <Route
                  path="*"
                  element={
                    <ModuleElement
                      resolvedModule={child}
                      parentModuleId={resolvedModule.id}
                    />
                  }
                />
              </Route>
            ))}
            <Route path="*" element={<ErrorPage />} />
          </Route>
        );
      }

      // Leaf module (schema page or component page without children).
      // `index` handles the exact match, `*` allows deeper sub-paths.
      return (
        <Route key={resolvedModule.id} path={resolvedModule.id}>
          <Route
            index
            element={<ModuleElement resolvedModule={resolvedModule} />}
          />
          <Route
            path="*"
            element={<ModuleElement resolvedModule={resolvedModule} />}
          />
        </Route>
      );
    })}
    <Route path="*" element={<ErrorPage />} />
  </Routes>
);
